import React, { useEffect, useState } from "react";
import { DEFAULT_SOCIAL } from "@/components/SocialLinks";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/apiClient";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function AdminSettings() {
  const { lang } = useLang();
  const [s, setS] = useState(null);
  const [retention, setRetention] = useState(null);
  const [pay, setPay] = useState(null);
  // Local editable fields for payments (never bind directly to secret masked fields)
  const [stripeSecret, setStripeSecret] = useState("");
  const [stripePub, setStripePub] = useState("");
  const [stripeEnabled, setStripeEnabled] = useState(true);
  const [midSecret, setMidSecret] = useState("");
  const [midClient, setMidClient] = useState("");
  const [midProd, setMidProd] = useState(false);
  const [midEnabled, setMidEnabled] = useState(true);
  const [ppClientId, setPpClientId] = useState("");
  const [ppSecret, setPpSecret] = useState("");
  const [ppProd, setPpProd] = useState(false);
  const [ppEnabled, setPpEnabled] = useState(true);
  const [ppWebhookId, setPpWebhookId] = useState("");
  const [activeGateway, setActiveGateway] = useState("");
  const [savingPay, setSavingPay] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(r => setS(r.data || {}));
    api.get("/admin/settings/retention").then(r => setRetention(r.data));
    api.get("/admin/settings/payments").then(r => {
      setPay(r.data);
      setStripePub(r.data?.stripe?.publishable_key || "");
      setStripeEnabled(r.data?.stripe?.enabled !== false);
      setMidClient(r.data?.midtrans?.client_key || "");
      setMidProd(r.data?.midtrans?.environment === "production");
      setMidEnabled(r.data?.midtrans?.enabled !== false);
      setPpClientId(r.data?.paypal?.client_id || "");
      setPpProd(r.data?.paypal?.environment === "production");
      setPpEnabled(r.data?.paypal?.enabled !== false);
      setPpWebhookId(r.data?.paypal?.webhook_id || "");
      setActiveGateway(r.data?.active_gateway || "");
    });
  }, []);

  if (!s) return <AdminLayout><div>…</div></AdminLayout>;
  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));
  const save = async () => { await api.put("/admin/settings", s); toast.success(lang === "id" ? "Tersimpan" : "Saved"); };
  const setR = (k, v) => setRetention((prev) => ({ ...prev, [k]: v }));
  const saveR = async () => { await api.put("/admin/settings/retention", retention); toast.success(lang === "id" ? "Kebijakan retensi tersimpan" : "Retention policy saved"); };

  const savePay = async () => {
    setSavingPay(true);
    try {
      const body = {
        stripe_publishable_key: stripePub,
        stripe_enabled: stripeEnabled,
        midtrans_client_key: midClient,
        midtrans_is_production: midProd,
        midtrans_enabled: midEnabled,
        paypal_client_id: ppClientId,
        paypal_is_production: ppProd,
        paypal_enabled: ppEnabled,
        paypal_webhook_id: ppWebhookId,
        active_gateway: activeGateway || "none",
      };
      if (stripeSecret && stripeSecret.trim() !== "") body.stripe_secret_key = stripeSecret.trim();
      if (midSecret && midSecret.trim() !== "") body.midtrans_server_key = midSecret.trim();
      if (ppSecret && ppSecret.trim() !== "") body.paypal_client_secret = ppSecret.trim();
      const { data } = await api.put("/admin/settings/payments", body);
      setPay(data);
      setStripeSecret(""); setMidSecret(""); setPpSecret("");
      toast.success(lang === "id" ? "Pengaturan pembayaran tersimpan" : "Payment settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setSavingPay(false); }
  };

  const stripe = pay?.stripe || {};
  const midtrans = pay?.midtrans || {};
  const paypal = pay?.paypal || {};

  return (
    <AdminLayout>
      <h1 className="font-display text-3xl tracking-tight mb-8">{lang === "id" ? "Pengaturan" : "Settings"}</h1>
      <div className="max-w-3xl space-y-6">
        {/* Brand & Meta */}
        <div className="p-6 rounded-2xl border border-border bg-card space-y-4">
          <div className="font-display text-lg">{lang === "id" ? "Merek & Meta" : "Brand & Meta"}</div>
          {[
            ["brand_name_id", lang === "id" ? "Nama brand (ID)" : "Brand name (ID)"],
            ["brand_name_en", lang === "id" ? "Nama brand (EN)" : "Brand name (EN)"],
            ["tagline_id", lang === "id" ? "Tagline (ID)" : "Tagline (ID)"],
            ["tagline_en", lang === "id" ? "Tagline (EN)" : "Tagline (EN)"],
          ].map(([k, label]) => (
            <div key={k}>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
              <Input data-testid={`settings-${k}`} value={s[k] || ""} onChange={(e) => set(k, e.target.value)} className="h-10" />
            </div>
          ))}
          {[["footer_note_id", "Footer (ID)"], ["footer_note_en", "Footer (EN)"]].map(([k, label]) => (
            <div key={k}>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
              <Textarea data-testid={`settings-${k}`} value={s[k] || ""} onChange={(e) => set(k, e.target.value)} rows={2} />
            </div>
          ))}

          {/* Support contact */}
          <div className="pt-4 border-t border-border">
            <div className="font-display text-base mb-3">{lang === "id" ? "Kontak Dukungan (ditampilkan di footer)" : "Support Contact (shown in footer)"}</div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Email dukungan" : "Support email"}</Label>
                <Input value={s.support_email || ""} onChange={(e) => set("support_email", e.target.value)} placeholder="support@yourdomain.com" className="h-10" data-testid="settings-support-email" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Teks bantuan (ID)" : "Support text (ID)"}</Label>
                <Input value={s.support_contact_text_id || ""} onChange={(e) => set("support_contact_text_id", e.target.value)} placeholder={lang === "id" ? "Butuh bantuan? Hubungi kami…" : "Need help? Contact us…"} className="h-10" data-testid="settings-support-text-id" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Teks bantuan (EN)" : "Support text (EN)"}</Label>
                <Input value={s.support_contact_text_en || ""} onChange={(e) => set("support_contact_text_en", e.target.value)} placeholder="Need help? Reach our support team." className="h-10" data-testid="settings-support-text-en" />
              </div>
            </div>
          </div>

          {/* Social media links */}
          <div className="pt-4 border-t border-border">
            <div className="font-display text-base mb-1">{lang === "id" ? "Media Sosial (ditampilkan di footer dan halaman hasil)" : "Social Media (shown in footer and result pages)"}</div>
            <p className="text-xs text-muted-foreground mb-3">{lang === "id" ? "Kosongkan untuk memakai link bawaan." : "Leave empty to use the built-in link."}</p>
            <div className="grid md:grid-cols-2 gap-3">
              {[
                ["social_instagram_url", "Instagram", DEFAULT_SOCIAL.social_instagram_url],
                ["social_tiktok_url", "TikTok", DEFAULT_SOCIAL.social_tiktok_url],
                ["social_facebook_url", "Facebook Page", "https://www.facebook.com/…"],
              ].map(([k, label, ph]) => (
                <div key={k}>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
                  <Input value={s[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph} className="h-10" data-testid={`settings-${k}`} />
                </div>
              ))}
            </div>
          </div>

          <Button onClick={save} data-testid="settings-save-btn" className="rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-cream-50">{lang === "id" ? "Simpan" : "Save"}</Button>
        </div>

        {/* Retention */}
        {retention && (
          <div className="p-6 rounded-2xl border border-border bg-card space-y-4">
            <div className="font-display text-lg">{lang === "id" ? "Kebijakan Retensi Data" : "Data Retention"}</div>
            <p className="text-xs text-muted-foreground -mt-2">{lang === "id" ? "Konfigurasi tidak menghapus data secara otomatis kecuali auto-cleanup diaktifkan." : "These settings do not delete data automatically unless auto-cleanup is enabled."}</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Respons belum selesai (hari)" : "Incomplete retention (days)"}</Label>
                <Input type="number" value={retention.incomplete_retention_days} onChange={(e) => setR("incomplete_retention_days", parseInt(e.target.value || 0))} className="h-10" data-testid="retention-incomplete-days" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Data teridentifikasi (hari)" : "Identifiable data (days)"}</Label>
                <Input type="number" value={retention.identifiable_retention_days} onChange={(e) => setR("identifiable_retention_days", parseInt(e.target.value || 0))} className="h-10" data-testid="retention-identifiable-days" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Anonimkan setelah (hari)" : "Anonymize after (days)"}</Label>
                <Input type="number" value={retention.anonymize_after_days ?? ""} onChange={(e) => setR("anonymize_after_days", e.target.value ? parseInt(e.target.value) : null)} placeholder="—" className="h-10" data-testid="retention-anonymize-days" />
              </div>
            </div>
            <div className="flex items-center gap-6 pt-1">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={retention.archived_in_backups} onCheckedChange={(v) => setR("archived_in_backups", v)} data-testid="retention-archive-backup" />
                {lang === "id" ? "Sertakan arsip di backup" : "Include archives in backup"}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={retention.auto_cleanup_enabled} onCheckedChange={(v) => setR("auto_cleanup_enabled", v)} data-testid="retention-auto-cleanup" />
                {lang === "id" ? "Auto cleanup" : "Auto cleanup"}
              </label>
            </div>
            <Button onClick={saveR} data-testid="retention-save-btn" className="rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-cream-50">{lang === "id" ? "Simpan Kebijakan" : "Save Policy"}</Button>
          </div>
        )}

        {/* Payments Gateway */}
        {pay && (
          <div className="p-6 rounded-2xl border border-border bg-card space-y-5" data-testid="payments-settings-card">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="font-display text-lg">{lang === "id" ? "Gateway Pembayaran" : "Payment Gateways"}</div>
              <span className="text-xs px-2 py-0.5 rounded-full border border-border bg-secondary/40" data-testid="payments-active-badge">
                {lang === "id" ? "Aktif" : "Active"}: <span className="font-semibold">{pay.active_gateway || "none"}</span>
              </span>
            </div>

            {/* Active gateway selector */}
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Provider utama untuk laporan berbayar" : "Primary provider for paid reports"}</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {[
                  { v: "midtrans", label: "Midtrans (QRIS, e-wallet, VA, dll.)" },
                  { v: "stripe", label: "Stripe (Kartu kredit / internasional)" },
                  { v: "none", label: lang === "id" ? "Nonaktifkan pembayaran" : "Disable payments" },
                ].map(o => (
                  <button
                    key={o.v}
                    onClick={() => setActiveGateway(o.v)}
                    className={`px-4 py-2 rounded-full border text-sm transition ${activeGateway === o.v ? "bg-teal-800 text-cream-50 border-teal-800" : "bg-card border-border hover:bg-secondary"}`}
                    data-testid={`payments-active-${o.v}`}
                  >{o.label}</button>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">
                {lang === "id"
                  ? "Sistem otomatis fallback ke provider yang aktif & terkonfigurasi jika pilihan Anda tidak tersedia."
                  : "The system automatically falls back to any enabled+configured provider if your selection is unavailable."}
              </p>
            </div>

            {/* Midtrans block */}
            <div className="mt-2 pt-4 border-t border-border space-y-3" data-testid="midtrans-block">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-display text-base">Midtrans</div>
                <div className="flex items-center gap-3 text-xs">
                  {midtrans.configured ? (
                    <span className={`px-2 py-0.5 rounded-full border ${midtrans.environment === "production" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`} data-testid="midtrans-status-badge">
                      {midtrans.environment === "production" ? (lang === "id" ? "Production" : "Production") : (lang === "id" ? "Sandbox" : "Sandbox")}
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full border bg-rose-50 border-rose-200 text-rose-700" data-testid="midtrans-status-badge">
                      {lang === "id" ? "Belum dikonfigurasi" : "Not configured"}
                    </span>
                  )}
                  <label className="flex items-center gap-2">
                    <Switch checked={midEnabled} onCheckedChange={setMidEnabled} data-testid="midtrans-enabled-switch" />
                    <span>{lang === "id" ? "Aktif" : "Enabled"}</span>
                  </label>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {lang === "id"
                  ? "Ambil Server Key & Client Key di Dashboard Midtrans → Settings → Access Keys. Pilih Sandbox untuk uji coba, Production untuk go-live."
                  : "Get Server Key & Client Key from Midtrans Dashboard → Settings → Access Keys. Use Sandbox for testing, Production for go-live."}
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Server Key ({lang === "id" ? "rahasia" : "secret"})</Label>
                  <Input
                    type="password"
                    autoComplete="off"
                    placeholder={midtrans.server_key_masked || "SB-Mid-server-… / Mid-server-…"}
                    value={midSecret}
                    onChange={(e) => setMidSecret(e.target.value)}
                    className="h-10 font-mono text-xs"
                    data-testid="midtrans-server-input"
                  />
                  <div className="text-[11px] text-muted-foreground mt-1 font-mono">
                    {lang === "id" ? "Tersimpan: " : "Stored: "}{midtrans.server_key_masked || "—"}
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Client Key</Label>
                  <Input
                    placeholder="SB-Mid-client-… / Mid-client-…"
                    value={midClient}
                    onChange={(e) => setMidClient(e.target.value)}
                    className="h-10 font-mono text-xs"
                    data-testid="midtrans-client-input"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={midProd} onCheckedChange={setMidProd} data-testid="midtrans-prod-switch" />
                <span>{lang === "id" ? "Gunakan environment Production" : "Use Production environment"}</span>
              </label>
              <div className="text-[11px] text-muted-foreground p-3 rounded-xl bg-secondary/40 border border-border">
                {lang === "id"
                  ? "Webhook URL yang perlu Anda set di dashboard Midtrans: "
                  : "Webhook URL to set in your Midtrans dashboard: "}
                <span className="font-mono break-all">{`${window.location.origin}/api/webhook/midtrans`}</span>
              </div>
              <div className="text-[11px] text-muted-foreground p-3 rounded-xl bg-amber-50/60 border border-amber-200">
                {lang === "id"
                  ? "Metode pembayaran (QRIS, GoPay, OVO, DANA, ShopeePay, VA, Kartu Kredit, Alfamart/Indomaret) diaktifkan di Dashboard Midtrans → Settings → Payment Channels. Semua saluran aktif akan otomatis muncul di popup Snap."
                  : "Payment methods (QRIS, GoPay, OVO, DANA, ShopeePay, VA, Credit Card, Alfamart/Indomaret) are activated in Midtrans Dashboard → Settings → Payment Channels. All enabled channels will appear automatically in the Snap popup."}
              </div>
            </div>

            {/* PayPal block */}
            <div className="mt-2 pt-4 border-t border-border space-y-3" data-testid="paypal-block">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-display text-base">{lang === "id" ? "PayPal (USD, pengunjung berbahasa Inggris)" : "PayPal (USD, English-language visitors)"}</div>
                <div className="flex items-center gap-3 text-xs">
                  {paypal.configured ? (
                    <span className={`px-2 py-0.5 rounded-full border ${paypal.environment === "production" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`} data-testid="paypal-status-badge">
                      {paypal.environment === "production" ? "Live" : "Sandbox"}
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full border bg-rose-50 border-rose-200 text-rose-700" data-testid="paypal-status-badge">
                      {lang === "id" ? "Belum dikonfigurasi" : "Not configured"}
                    </span>
                  )}
                  <label className="flex items-center gap-2">
                    <Switch checked={ppEnabled} onCheckedChange={setPpEnabled} data-testid="paypal-enabled-switch" />
                    <span>{lang === "id" ? "Aktif" : "Enabled"}</span>
                  </label>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                {lang === "id"
                  ? "Buat aplikasi di developer.paypal.com → Apps & Credentials, lalu salin Client ID dan Secret. Pengunjung berbahasa Inggris membayar Harga USD tiap instrumen lewat PayPal; pengunjung berbahasa Indonesia tetap memakai provider utama di atas."
                  : "Create an app at developer.paypal.com → Apps & Credentials and copy its Client ID and Secret. English-language visitors pay each instrument's USD price through PayPal; Indonesian-language visitors keep using the primary provider above."}
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Client ID</Label>
                  <Input value={ppClientId} onChange={(e) => setPpClientId(e.target.value)} className="h-10 font-mono text-xs" data-testid="paypal-client-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Secret ({lang === "id" ? "rahasia" : "secret"})</Label>
                  <Input
                    type="password"
                    autoComplete="off"
                    placeholder={paypal.client_secret_masked || "…"}
                    value={ppSecret}
                    onChange={(e) => setPpSecret(e.target.value)}
                    className="h-10 font-mono text-xs"
                    data-testid="paypal-secret-input"
                  />
                  <div className="text-[11px] text-muted-foreground mt-1 font-mono">
                    {lang === "id" ? "Tersimpan: " : "Stored: "}{paypal.client_secret_masked || "—"}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Webhook ID</Label>
                  <Input value={ppWebhookId} onChange={(e) => setPpWebhookId(e.target.value)} className="h-10 font-mono text-xs" data-testid="paypal-webhook-input" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={ppProd} onCheckedChange={setPpProd} data-testid="paypal-prod-switch" />
                <span>{lang === "id" ? "Gunakan akun Live (bukan Sandbox)" : "Use Live account (not Sandbox)"}</span>
              </label>
              <div className="text-[11px] text-muted-foreground p-3 rounded-xl bg-secondary/40 border border-border">
                {lang === "id"
                  ? "Tambahkan webhook di aplikasi PayPal dengan URL berikut, centang event Checkout order approved, Payment capture completed, denied, pending, refunded dan reversed, lalu tempel Webhook ID-nya di atas: "
                  : "Add a webhook in your PayPal app with this URL, tick Checkout order approved and Payment capture completed, denied, pending, refunded and reversed, then paste its Webhook ID above: "}
                <span className="font-mono break-all">{`${window.location.origin}/api/webhook/paypal`}</span>
              </div>
            </div>

            {/* Stripe block */}
            <div className="mt-2 pt-4 border-t border-border space-y-3" data-testid="stripe-block">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-display text-base">Stripe</div>
                <div className="flex items-center gap-3 text-xs">
                  {stripe.configured ? (
                    <span className={`px-2 py-0.5 rounded-full border ${stripe.mode === "live" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`} data-testid="stripe-status-badge">
                      {stripe.mode === "live" ? "Live" : "Test"}
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full border bg-rose-50 border-rose-200 text-rose-700" data-testid="stripe-status-badge">
                      {lang === "id" ? "Belum dikonfigurasi" : "Not configured"}
                    </span>
                  )}
                  <span className="px-2 py-0.5 rounded-full border border-border bg-secondary/40" data-testid="stripe-source-badge">
                    {lang === "id" ? "Sumber" : "Source"}: {stripe.source}
                  </span>
                  <label className="flex items-center gap-2">
                    <Switch checked={stripeEnabled} onCheckedChange={setStripeEnabled} data-testid="stripe-enabled-switch" />
                    <span>{lang === "id" ? "Aktif" : "Enabled"}</span>
                  </label>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Stripe Secret Key (rahasia)" : "Stripe Secret Key (secret)"}</Label>
                <Input
                  type="password"
                  autoComplete="off"
                  placeholder={stripe.secret_key_masked || "sk_test_… / sk_live_…"}
                  value={stripeSecret}
                  onChange={(e) => setStripeSecret(e.target.value)}
                  className="h-10 font-mono text-xs"
                  data-testid="stripe-secret-input"
                />
                <div className="text-[11px] text-muted-foreground mt-1 font-mono">
                  {lang === "id" ? "Tersimpan: " : "Stored: "}{stripe.secret_key_masked || "—"}
                  {stripe.has_db_override && <span className="ml-2 text-emerald-700">✓ DB override</span>}
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{lang === "id" ? "Stripe Publishable Key (opsional)" : "Stripe Publishable Key (optional)"}</Label>
                <Input placeholder="pk_test_… / pk_live_…" value={stripePub} onChange={(e) => setStripePub(e.target.value)} className="h-10 font-mono text-xs" data-testid="stripe-publishable-input" />
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <Button onClick={savePay} disabled={savingPay} data-testid="payments-save-btn" className="rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-cream-50">
                {savingPay ? (lang === "id" ? "Menyimpan…" : "Saving…") : (lang === "id" ? "Simpan Pengaturan Pembayaran" : "Save Payment Settings")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
